import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { Grid, CircularProgress, Divider, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Paper, InputBase, Box, Button, Alert, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import GetAppIcon from "@mui/icons-material/GetApp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArticleIcon from "@mui/icons-material/Article";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import { convertMessageToHtml } from "../../utilities/helpers";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const ChatWithFiles = ({ uploadFileParams }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { assignmentId } = uploadFileParams;
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', content, citations, isStreaming }
  const [isStreaming, setIsStreaming] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);

  const [attachments, setAttachments] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [aiActionLog, setAiActionLog] = useState([]);

  const messagesEndRef = useRef(null);

  const tenantId = useSelector((state) => {
    return state.tenantId;
  });

  const loggedInUser = useSelector((state) => {
    return state.user;
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: isStreaming ? "instant" : "smooth" });
  }, [messages, isStreaming]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await ieqipCoreServiceApi.get(`/api/assignmentdocuments/assignmentid/${assignmentId}`, {
          headers: {
            "x-eqip-tenantid": tenantId,
          },
        });
        setAttachments(res.data);
        setLoading(false);
        setSelectedRows(null)
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    })();
  }, [assignmentId, tenantId]);

  const getDocuments = async () => {
    const res = await ieqipCoreServiceApi.get(`/api/assignmentdocuments/assignmentid/${assignmentId}`, {
      headers: {
        "x-eqip-tenantid": tenantId,
      },
    });
    return res.data;
  };

  const handleUploadFile = async (event) => {
    const foundFile = event.target.files[0];
    if (foundFile.size > MAX_FILE_SIZE) {
      setErrorMessage(`File "${foundFile.name}" exceeds the 10MB size limit.`);
      event.target.value = '';
      return;
    }
    setLoading(true);
    setErrorMessage(null);
    try {
      //1. Get upload presigned url
      const formData = new FormData();
      formData.append("file", foundFile);
      formData.append("bucket_name", "eqip-document-dev");
      const responseUploadedFile = await ieqipCoreServiceApi.post("/api/s3/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      //2. Insert record to document table in database
      await ieqipCoreServiceApi.post(`/api/assignmentdocuments`, {
        assignment_document_uid: uuidv4(),
        tenant_id: tenantId,
        assignment_id: assignmentId,
        original_filename: responseUploadedFile.data.original_filename,
        uploaded_filename: responseUploadedFile.data.uploaded_filename,
        etag: responseUploadedFile.data.response.ETag,
        created_date: new Date().toISOString(),
        created_by: loggedInUser.email,
      });

      //3. Reload document list
      setAttachments(await getDocuments());
    } catch (error) {
      console.error("Uploaded failed", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (attachment) => {
    setLoading(true);
    try {
      const response = await ieqipCoreServiceApi.get(`/api/s3/download/${attachment.uploaded_filename}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.original_filename || attachment.uploaded_filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (attachment) => {
    setLoading(true);
    try {
      //1. remove file from S3
      await ieqipCoreServiceApi.delete(`/api/s3/delete/${attachment.uploaded_filename}`);

      //2. delete document from database
      await ieqipCoreServiceApi.delete(`/api/assignmentdocuments/${attachment.assignment_document_uid}`, {
        headers: {
          "x-eqip-tenantid": tenantId,
        },
      });

      setAttachments(await getDocuments());
    } catch (error) {
      console.error("Delete failed", error);
      setLoading(false);
    }
    setLoading(false);
  };

  const handleAI = async () => {
    if (!selectedRows || selectedRows?.length === 0) {
      setErrorMessage("Please select at least one file to process.");
      return;
    }
    setLoading(true);
    setIsProcessingAI(true);
    setAiActionLog([]);
    setErrorMessage(null);
    setInput("");
    setMessages([]);

    const log = [];
    const addLog = (message, status = 'pending') => {
      log.push({ id: log.length, message, status });
      setAiActionLog([...log]);
    };
    const completeLastLog = (status = 'done') => {
      log[log.length - 1].status = status;
      setAiActionLog([...log]);
    };

    try {
      const s3Keys = selectedRows
        .map((id) => attachments.find((a) => a.id === id))
        .filter(Boolean)
        .map((a) => a.uploaded_filename);

      if (s3Keys.length === 0) {
        throw new Error("No valid files to process");
      }

      addLog(`Processing ${s3Keys.length} file(s) with AI Assistant...`);
      const { data: jobData } = await ieqipCoreServiceApi.post(
        "/api/openai/assistant/uploadfile/froms3",
        { s3_keys: s3Keys }
      );

      const jobId = jobData.job_id;
      await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          try {
            const { data: jobStatus } = await ieqipCoreServiceApi.get(
              `/api/openai/assistant/job/${jobId}`
            );
            if (jobStatus.status === "completed") {
              clearInterval(poll);
              resolve();
            } else if (jobStatus.status === "failed") {
              clearInterval(poll);
              reject(new Error(jobStatus.error || "AI processing failed"));
            }
          } catch (err) {
            clearInterval(poll);
            reject(err);
          }
        }, 4000);
      });
      completeLastLog('done');

      addLog("AI Assistant is ready.", 'done');
      setIsProcessingAI(false);
      setShowAIChat(true);
    } catch (error) {
      completeLastLog('error');
      const detail = error.response?.data?.detail || error.message;
      setErrorMessage(`AI processing failed: ${detail}`);
      setIsProcessingAI(false);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setShowAIChat(false);
    setSelectedRows(null);
    setMessages([]);
  };

  const onHandleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;

    const question = input.trim();
    setInput("");
    setIsStreaming(true);

    setMessages((prev) => [
      ...prev,
      { role: "user", content: question },
      { role: "assistant", content: "", citations: null, isStreaming: true },
    ]);

    try {
      const baseUrl = process.env.REACT_APP_IEQIP_CORE_SERVICE_API_URL;
      const response = await fetch(`${baseUrl}/api/openai/assistant/ask/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullMessage = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.delta) {
                fullMessage += data.delta;
                const clean = fullMessage.replace(/【\d+:\d+†[^】]*】/g, "");
                // flushSync bypasses React 18 automatic batching so each token
                // triggers its own render — giving the ChatGPT streaming effect.
                flushSync(() => {
                  setMessages((prev) => {
                    const updated = [...prev];
                    updated[updated.length - 1] = { ...updated[updated.length - 1], content: clean };
                    return updated;
                  });
                });
              } else if (data.citations) {
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { ...updated[updated.length - 1], citations: data.citations };
                  return updated;
                });
              }
            } catch (_) { /* ignore parse errors */ }
          }
        }
      }
    } catch (error) {
      console.error("Send failed", error);
    } finally {
      setIsStreaming(false);
      setMessages((prev) => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1] = { ...updated[updated.length - 1], isStreaming: false };
        }
        return updated;
      });
    }
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const columns = [
    {
      field: "original_filename",
      headerName: "File Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "assignment_uid",
      headerName: "Actions",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Box>
            <IconButton color="secondary" aria-label="delete file" component="span" onClick={() => handleDeleteFile(row)}>
              <DeleteForeverIcon />
            </IconButton>
            <IconButton color="secondary" aria-label="download file" component="span" onClick={() => handleDownloadFile(row)}>
              <GetAppIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      {/* File list + upload — hidden while chat is active */}
      {!showAIChat && !isProcessingAI && (
        <>
          <Grid item xs={12} style={{ marginBottom: "0.5em" }}>
            <input id="icon-button-file" type="file" onChange={handleUploadFile} />
          </Grid>
          <Box display="flex" justifyContent="end">
            <Button disabled={loading} color="primary" variant="contained" startIcon={<AutoAwesomeIcon />} onClick={() => handleAI()}>
              AI Assistant
            </Button>
          </Box>
        </>
      )}

      {showAIChat && (
        <Box display="flex" justifyContent="end">
          <Button color="primary" variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack} size="small">
            Back
          </Button>
        </Box>
      )}

      {errorMessage && (
        <Box sx={{ mt: 1 }}>
          <Alert onClose={() => setErrorMessage("")} severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {/* AI setup progress */}
      {isProcessingAI && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Processing with AI Assistant...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={aiActionLog.length === 0 ? 0 : (aiActionLog.filter(l => l.status === "done").length / aiActionLog.length) * 100}
            sx={{ borderRadius: 1, height: 8, mb: 2 }}
          />
          <List dense disablePadding>
            {aiActionLog.map((entry) => (
              <ListItem key={entry.id} disableGutters sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {entry.status === "done" && <CheckCircleOutlineIcon fontSize="small" color="success" />}
                  {entry.status === "error" && <ErrorOutlineIcon fontSize="small" color="error" />}
                  {entry.status === "pending" && <CircularProgress size={16} />}
                </ListItemIcon>
                <ListItemText
                  primary={entry.message}
                  primaryTypographyProps={{ variant: "body2", color: entry.status === "error" ? "error" : "text.primary" }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* File table */}
      {!showAIChat && !isProcessingAI && (
        <Box
          m="10px 0 0 0"
          height="50vh"
          sx={{
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .name-column--cell": { color: colors.blueAccent[600] },
            "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.grey[900], borderBottom: "none" },
            "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
            "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.grey[900] },
            "& .MuiCheckbox-root": { color: `${colors.grey[500]} !important` },
          }}
        >
          <DataGrid
            checkboxSelection
            rows={attachments}
            columns={columns}
            loading={loading}
            onSelectionModelChange={(selectionModel) => handleSelectionChange(selectionModel)}
          />
        </Box>
      )}

      {/* Chat view */}
      {showAIChat && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "62vh", mt: 1 }}>
          {/* Scrollable messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              p: 1,
              bgcolor: colors.primary[400],
              borderRadius: 1,
            }}
          >
            {messages.length === 0 && (
              <Box sx={{ m: "auto", textAlign: "center", opacity: 0.5 }}>
                <AutoAwesomeIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body2">Ask anything about your uploaded files</Typography>
              </Box>
            )}

            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <Box
                  sx={{
                    maxWidth: "80%",
                    px: 2,
                    py: 1.5,
                    borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    bgcolor: msg.role === "user" ? "primary.main" : colors.grey[900],
                    color: msg.role === "user" ? "#fff" : "text.primary",
                    boxShadow: 1,
                  }}
                >
                  {msg.role === "assistant" ? (
                    <>
                      <div
                        dangerouslySetInnerHTML={{ __html: convertMessageToHtml(msg.content) }}
                        style={{ lineHeight: 1.6 }}
                      />
                      {msg.isStreaming && (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            width: "2px",
                            height: "1em",
                            bgcolor: "text.primary",
                            ml: 0.25,
                            verticalAlign: "text-bottom",
                            "@keyframes blink": {
                              "0%, 100%": { opacity: 1 },
                              "50%": { opacity: 0 },
                            },
                            animation: "blink 0.8s step-end infinite",
                          }}
                        />
                      )}
                      {msg.citations && msg.citations.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Divider sx={{ mb: 1, opacity: 0.3 }} />
                          <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.7, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            Sources
                          </Typography>
                          <List dense disablePadding sx={{ mt: 0.5 }}>
                            {msg.citations.map((filename, i) => (
                              <ListItem key={i} disableGutters sx={{ py: 0.1 }}>
                                <ListItemIcon sx={{ minWidth: 22 }}>
                                  <ArticleIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={filename}
                                  primaryTypographyProps={{ variant: "caption", sx: { opacity: 0.8 } }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2">{msg.content}</Typography>
                  )}
                </Box>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input bar */}
          <Paper
            component="form"
            onSubmit={onHandleSend}
            elevation={3}
            sx={{ p: "4px 8px", display: "flex", alignItems: "center", mt: 1, borderRadius: 3 }}
          >
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Ask about your files…"
              inputProps={{ "aria-label": "Ask about your files" }}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onHandleSend(e);
                }
              }}
              disabled={isStreaming}
              multiline
              maxRows={4}
            />
            <IconButton
              type="submit"
              color="primary"
              disabled={isStreaming || !input.trim()}
              sx={{ p: "8px" }}
            >
              {isStreaming ? <CircularProgress size={22} /> : <SendIcon />}
            </IconButton>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default ChatWithFiles;
